import { useCallback, useEffect, useRef, useState } from 'react';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { api, ApiError } from '../../api.ts';
import { TopBar } from '../../components/TopBar.tsx';
import { BottomNav } from '../../components/BottomNav.tsx';
import { PAYMENT_ERRORS, getStripeConfig } from '../../stripeClient.ts';

interface Status {
  configured: boolean;
  mode: 'test' | 'live' | null;
  connected: boolean;
  detailsSubmitted: boolean;
  payoutsEnabled: boolean;
  requirementsDue: string[];
  bankLast4: string | null;
}

/**
 * Versements de l'extra. L'identité et l'IBAN se saisissent une seule fois, ici, dans un formulaire de Stripe intégré
 * à Mon Extra : pas de compte ni de connexion Stripe. Ensuite, chaque mission validée est virée automatiquement.
 */
export function ExtraPayouts() {
  const [status, setStatus] = useState<Status | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const container = useRef<HTMLDivElement>(null);

  const refresh = useCallback(() => {
    api.get<Status>('/payments/extras/stripe/status').then(setStatus);
  }, []);
  useEffect(refresh, [refresh]);

  useEffect(() => {
    if (!open || !container.current) return;
    const host = container.current;
    let cancelled = false;
    (async () => {
      try {
        const config = await getStripeConfig();
        if (!config.publishableKey) throw new ApiError('STRIPE_NOT_CONFIGURED', 503);
        const instance = loadConnectAndInitialize({
          publishableKey: config.publishableKey,
          fetchClientSecret: async () => (await api.post<{ clientSecret: string }>('/payments/extras/stripe/account-session')).clientSecret,
          appearance: { variables: { colorPrimary: '#d9622b', borderRadius: '12px' } },
          locale: 'fr-FR',
        } as Parameters<typeof loadConnectAndInitialize>[0]);
        const element = instance.create('account-onboarding');
        element.setOnExit(() => {
          setOpen(false);
          refresh();
        });
        if (!cancelled) host.replaceChildren(element);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? PAYMENT_ERRORS[err.code] ?? 'Le formulaire est indisponible.' : 'Le formulaire est indisponible.');
          setOpen(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      host.replaceChildren();
    };
  }, [open, refresh]);

  // Repli : le même formulaire hébergé par Stripe, si l'intégration ne se charge pas.
  async function redirect() {
    const res = await api.post<{ url: string }>('/payments/extras/stripe/onboarding');
    window.location.href = res.url;
  }

  const ready = status?.payoutsEnabled;
  const started = status?.connected && status.detailsSubmitted && !ready;

  return (
    <div className="app-shell">
      <TopBar title="Mes versements" back />
      <div className="screen-content">
        {!status && <div className="empty-state">Chargement…</div>}
        {status && !status.configured && (
          <div className="badge badge-pending" style={{ padding: '12px 14px', lineHeight: 1.5 }}>
            Les versements ne sont pas encore activés sur Mon Extra. Vous serez prévenu dès que ce sera le cas.
          </div>
        )}

        {status?.configured && (
          <>
            <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <div style={{ fontWeight: 800 }}>Recevoir mes paiements</div>
                <span className={`badge ${ready ? 'badge-available' : started ? 'badge-pending' : 'badge-neutral'}`}>
                  {ready ? 'Activés' : started ? 'Vérification en cours' : 'À configurer'}
                </span>
              </div>
              {ready ? (
                <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  Tout est prêt. Après chaque mission validée, vous êtes payé automatiquement{status.bankLast4 ? ` sur votre compte bancaire •••• ${status.bankLast4}` : ''}. Vous n'avez rien à faire et aucun compte Stripe à consulter.
                </div>
              ) : (
                <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  Pour être payé, la réglementation impose de vérifier votre identité et votre compte bancaire. C'est à faire <strong>une seule fois</strong>, ici, en quelques minutes : vous n'avez pas à créer de compte ni à vous connecter à Stripe. Préparez une pièce d'identité et votre IBAN.
                  {started && status.requirementsDue.length > 0 && ` Il reste ${status.requirementsDue.length} information(s) à fournir.`}
                </div>
              )}
              {status.mode === 'test' && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Mode test : aucun vrai virement n'est effectué.</div>}
              {error && <div className="error-banner">{error}</div>}
              {!open && (!ready || status.requirementsDue.length > 0) && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => { setError(null); setOpen(true); }}>{started ? 'Compléter mes informations' : 'Configurer mes versements'}</button>
                  <button className="btn btn-ghost btn-sm" onClick={redirect}>Ouvrir sur le site de Stripe</button>
                </div>
              )}
            </div>
            {open && <div ref={container} style={{ minHeight: 320 }} aria-label="Formulaire de vérification" />}
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
